import type {
  Agent,
  AgentCreate,
  AgentDeleteImpact,
  AgentRouteDecision,
  AgentRouteOutcome,
  AgentSkill,
  AgentSkillAttach,
  AgentUpdate,
  DataResponse,
  PaginatedResponse,
  SharePointDocumentMeta,
} from "@/types";
import { api } from "../api";

export const agentService = {
  list: (params?: {
    page?: number;
    pageSize?: number;
    visibility?: string;
    teamUuid?: string;
    projectUuid?: string;
    excludeProjectAgents?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", String(params.page));
    if (params?.pageSize) searchParams.set("page_size", String(params.pageSize));
    if (params?.visibility) searchParams.set("visibility", params.visibility);
    if (params?.teamUuid) searchParams.set("team_uuid", params.teamUuid);
    if (params?.projectUuid) searchParams.set("project_uuid", params.projectUuid);
    if (params?.excludeProjectAgents) searchParams.set("exclude_project_agents", "true");
    return api.get<PaginatedResponse<Agent>>(`/agents?${searchParams}`);
  },
  get: (agentId: string) => api.get<DataResponse<Agent>>(`/agents/${agentId}`),
  create: (data: AgentCreate) => api.post<DataResponse<Agent>>("/agents", data),
  update: (agentId: string, data: AgentUpdate) => api.patch<DataResponse<Agent>>(`/agents/${agentId}`, data),
  delete: (agentId: string) => api.delete(`/agents/${agentId}`),
  /** What deleting this agent would affect (imported sibling skill). */
  deleteImpact: (agentId: string) => api.get<DataResponse<AgentDeleteImpact>>(`/agents/${agentId}/delete-impact`),
  /** Ask the auto-router which agent should answer a new chat's first message (MHS-627). */
  route: (message: string, projectUuid?: string, signal?: AbortSignal) =>
    api.post<DataResponse<AgentRouteDecision>>("/agents/route", { message, projectUuid }, { signal }),
  /** Record what the user did after a routing decision. */
  recordRouteOutcome: (
    decisionUuid: string,
    body: { outcome: AgentRouteOutcome; sessionUuid?: string | null; agentUuid?: string | null },
  ) => api.post<void>(`/agents/route/${decisionUuid}/outcome`, body),
  // Stream agent response with tool execution support
  stream: (
    // NOSONAR - Many optional params needed to match backend API contract
    agentId: string,
    message: string,
    sessionUuid?: string,
    sharepointDocuments?: SharePointDocumentMeta[],
    sharepointContent?: string,
    sharepointSlideImages?: string[],
    projectUuid?: string,
    imageAttachments?: string[],
    documentAttachments?: string[],
    routeDecisionUuid?: string,
    reasoningEffort?: string,
  ) =>
    api.post<{ operation_id: string; session_uuid: string }>(`/agents/${agentId}/stream`, {
      message,
      session_uuid: sessionUuid,
      projectUuid: projectUuid ?? undefined,
      sharepointDocuments: sharepointDocuments ?? undefined,
      sharepointContent: sharepointContent ?? undefined,
      sharepointSlideImages: sharepointSlideImages?.length ? sharepointSlideImages : undefined,
      imageAttachments: imageAttachments?.length ? imageAttachments : undefined,
      documentAttachments: documentAttachments?.length ? documentAttachments : undefined,
      routeDecisionUuid,
      reasoningEffort,
    }),
  // Invoke agent (non-streaming) with tool execution support
  invoke: (agentId: string, message: string, sessionUuid?: string) =>
    api.post<
      DataResponse<{
        content: string;
        sessionUuid: string;
        messageId: number;
        iterations: number;
        totalTokens: number;
        toolCalls: Array<{ toolName: string; arguments: Record<string, unknown>; result: string }>;
      }>
    >(`/agents/${agentId}/invoke`, { message, session_uuid: sessionUuid }),
  // Agent Skills
  listSkills: (agentId: string) => api.get<DataResponse<AgentSkill[]>>(`/agents/${agentId}/skills`),
  attachSkills: (agentId: string, skills: AgentSkillAttach[]) =>
    api.post<DataResponse<AgentSkill[]>>(`/agents/${agentId}/skills`, skills),
  updateSkill: (agentId: string, skillUuid: string, data: Partial<AgentSkillAttach>) =>
    api.patch<DataResponse<AgentSkill>>(`/agents/${agentId}/skills/${skillUuid}`, data),
  detachSkill: (agentId: string, skillUuid: string) => api.delete(`/agents/${agentId}/skills/${skillUuid}`),
};
